"use client";
import { useMemo } from "react";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem as RadixSelectItem,
  SelectValue
} from "@gettactic/components/src/primitives/Select";
import { UnifiedSelect, type SelectItem } from "@cmp-src/form/UnifiedSelect";
import { useIsMobile } from "@cmp-src/hooks/use-mobile";
import { ArrowDownIcon } from "@cmp-src/icons/icons";
import { cn } from "@gettactic/helpers";

type Props = {
  floor: string | null;
  setFloor: React.Dispatch<React.SetStateAction<string | null>>;
  floors: { id: string | null; name: string }[];
  className?: string;
  classNamePrefix?: string;
  readonly?: boolean;
  customIcon?: React.ReactNode;
};

export function FloorFilterDropdown({
  floors,
  setFloor,
  floor,
  className = "",
  classNamePrefix = "react-select",
  readonly = false,
  customIcon
}: Props) {
  const defaultFloor = {
    value: null,
    label: "-- All floors --"
  };
  const options = useMemo(() => {
    const floorValues = floors.map((x) => ({ value: x.id, label: x.name }));
    floorValues.unshift(defaultFloor);
    return floorValues;
  }, [floors]);
  const value = options.find((x) => x.value === floor);

  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Select
        value={value?.value || undefined}
        onValueChange={(id) => {
          if (readonly) {
            return;
          }
          setFloor((id === "all" ? "" : id) ?? "");
        }}
        disabled={readonly}
      >
        <SelectTrigger
          variant={"gray"}
          className={cn("mt-1 min-h-[42px]", className)}
        >
          <SelectValue className="truncate" placeholder="Select floor" />
          {customIcon || <ArrowDownIcon className="h-4 w-4 opacity-50" />}
        </SelectTrigger>
        <SelectContent>
          {options.map((floor) => (
            <RadixSelectItem
              key={floor.value || "all"}
              value={floor.value || "all"}
            >
              {floor.label}
            </RadixSelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const items: SelectItem[] = options.map((opt) => ({
    id: opt.value || "all",
    name: opt.label
  }));
  const currentValue = typeof value === "undefined" ? defaultFloor : value;
  const selectedItem = currentValue
    ? (items.find((item) => item.id === (currentValue.value || "all")) ??
      items[0])
    : items[0];

  const triggerContent = (
    <button
      type="button"
      className={cn(
        "flex h-10 min-w-[200px] w-full max-w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-sm overflow-hidden relative border border-neutral-300 bg-white hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
        className
      )}
    >
      <span className="truncate min-w-0 flex-1 text-left">
        <span className="absolute inset-2.5 mr-8 truncate">{selectedItem?.name || "Select floor"}</span>
      </span>
      <span className="shrink-0">{customIcon || <ArrowDownIcon className="h-4 w-4 opacity-50" />}</span>
    </button>
  );

  return (
    <UnifiedSelect
      items={items}
      value={selectedItem}
      onSelect={(item) => {
        if (readonly) return;
        setFloor(item?.id === "all" ? null : (item?.id ?? null));
      }}
      disabled={readonly}
      searchable
      trigger={triggerContent}
    />
  );
}
